import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Globe2, Wand2, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DIETARY_STYLES, HEALTH_CONDITIONS, ChipSelect } from "@/components/admin/DietOptions";
import { INDIAN_REGIONS } from "@/data/diet";

export const GS_CUISINES = [
  ["indian", "Indian"], ["american", "American"], ["african", "African"],
  ["middle_eastern", "Middle Eastern"], ["mediterranean", "Mediterranean"],
  ["latin", "Latin"], ["japanese", "Japanese"],
];
const ADMIN_EXTRA = [["ladakhi", "Ladakhi"], ["tibetan", "Tibetan"], ["chinese", "Chinese"]];
const FESTIVE = [["navratri", "Navratri (Vrat)"], ["ramadan", "Ramadan"], ["christmas", "Christmas"]];
const ADMIN_CUISINES = [...GS_CUISINES, ...ADMIN_EXTRA, ...FESTIVE];
const EXTRA_IDS = ADMIN_EXTRA.map(([id]) => id);
const LABEL = Object.fromEntries(ADMIN_CUISINES);
const RLABEL = Object.fromEntries(INDIAN_REGIONS);

const prefDays = (pref) => {
  if (pref?.mode === "single" && pref.single) return Array(7).fill(pref.single);
  if (pref?.mode === "mix" && Array.isArray(pref.days)) return Array.from({ length: 7 }, (_, i) => pref.days[i] || "indian");
  return Array(7).fill("indian");
};

const prefRegions = (pref) => {
  if (pref?.mode === "single") return Array(7).fill(pref.single === "indian" ? pref.single_region || "" : "");
  if (pref?.mode === "mix" && Array.isArray(pref.days)) return Array.from({ length: 7 }, (_, i) => (pref.days[i] === "indian" && pref.day_regions?.[i]) || "");
  return Array(7).fill("");
};

const EMPTY_T = { kcal: "", protein: "", carbs: "", fat: "", fibre: "" };

export const GlobalCuration = ({ api, clientId, assessment, onDone }) => {
  const pref = assessment?.food?.global_cuisine || null;
  const [days, setDays] = useState(prefDays(pref));
  const [regions, setRegions] = useState(prefRegions(pref));
  const [title, setTitle] = useState("JAY 7™ Global Week");
  const [t, setT] = useState(EMPTY_T);
  const [styles, setStyles] = useState([]);
  const [conds, setConds] = useState([]);
  const [allergy, setAllergy] = useState("");
  const [busy, setBusy] = useState(false);

  const loadTargets = () =>
    api().get(`/admin/diet/clients/${clientId}/tracker`).then((r) => {
      const s = r.data.targets || {};
      setT({ kcal: s.kcal ?? "", protein: s.protein ?? "", carbs: s.carbs ?? "", fat: s.fat ?? "", fibre: s.fibre ?? "" });
    }).catch(() => {});
  useEffect(() => { loadTargets(); }, [clientId]); // eslint-disable-line

  const setTk = (k) => (e) => setT({ ...t, [k]: e.target.value });
  const num = (v) => (v === "" || v == null ? null : parseFloat(v));

  const saveTargets = async () => {
    try {
      await api().post(`/admin/diet/clients/${clientId}/targets`, {
        daily_kcal: num(t.kcal), daily_protein: num(t.protein), daily_carbs: num(t.carbs), daily_fat: num(t.fat), daily_fibre: num(t.fibre),
      });
      toast.success("Client's daily targets saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save targets"); }
  };

  const resetAll = () => {
    setDays(prefDays(pref));
    setRegions(prefRegions(pref));
    setTitle("JAY 7™ Global Week");
    setStyles([]); setConds([]); setAllergy("");
    loadTargets();
    toast.info("Curation form reset");
  };

  const customised = styles.length > 0 || conds.length > 0 || allergy.trim() || t.kcal || t.protein || t.carbs || t.fat || t.fibre
    || days.some((c) => EXTRA_IDS.includes(c));

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api().post(`/admin/diet/clients/${clientId}/global-plan`, {
        title, days: days.map((cuisine, i) => ({ day: i + 1, cuisine, region: cuisine === "indian" && regions[i] ? regions[i] : null })),
        target_kcal: num(t.kcal), protein_g: num(t.protein), carbs_g: num(t.carbs), fat_g: num(t.fat), fibre_g: num(t.fibre),
        dietary_styles: styles, health_conditions: conds, allergy_note: allergy,
      }, { timeout: 240000 });
      toast.success(`"${r.data.title}" v${r.data.version} created — client emailed & WhatsApp queued`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request timed out — a custom AI plan may still have been created. Re-open this client to check their documents before retrying.");
    } finally { setBusy(false); }
  };

  const regionTag = (c, r) => (c === "indian" && r ? ` (${RLABEL[r] || r})` : "");
  const prefText = !pref ? "No cuisine preference captured in assessment — defaults shown."
    : pref.mode === "single" ? `Client prefers ONE cuisine all week: ${(LABEL[pref.single] || pref.single || "—") + regionTag(pref.single, pref.single_region)}`
    : `Client prefers a MIX: ${(pref.days || []).map((c, i) => c ? `D${i + 1} ${(LABEL[c] || c) + regionTag(c, pref.day_regions?.[i])}` : null).filter(Boolean).join(", ") || "—"}`;

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5 mt-5" data-testid="global-curation">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-medium flex items-center gap-2"><Globe2 size={15} className="text-[#89937C]" /> Global Series Curation — next 7 days</div>
        <button data-testid="curation-reset" onClick={resetAll}
          className="inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 border border-[#E5E2D9] text-[10px] font-medium text-[#5C665A] hover:border-[#89937C]">
          <RotateCcw size={11} /> Refresh / Clear
        </button>
      </div>
      <p className="mt-1 text-xs text-[#C57B57] font-medium" data-testid="curation-pref">{prefText}</p>

      <div className="mt-3 rounded-xl bg-[#F7F5F0] p-3" data-testid="curation-targets">
        <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-2">Client's daily targets — custom calories & macros</div>
        <div className="flex flex-wrap items-center gap-2">
          {[["kcal", "kcal"], ["protein", "Protein g"], ["carbs", "Carbs g"], ["fat", "Fat g"], ["fibre", "Fibre g"]].map(([k, ph]) => (
            <input key={k} type="number" data-testid={`target-${k}`} placeholder={ph} value={t[k]} onChange={setTk(k)}
              className="w-24 h-9 bg-white border border-[#E5E2D9] rounded-lg px-2 text-xs" />
          ))}
          <button data-testid="target-save" onClick={saveTargets}
            className="rounded-full px-4 py-2 bg-[#2D372B] text-white text-[10px] font-medium hover:bg-[#89937C]">Save targets</button>
        </div>
        <p className="mt-1.5 text-[10px] text-[#9AA294]">Saved targets drive the client's tracker progress bars AND are used as the calorie/macro brief for the generated plan.</p>
      </div>

      <div className="mt-3" data-testid="curation-styles">
        <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-1.5">Dietary style / eating pattern</div>
        <ChipSelect options={DIETARY_STYLES} value={styles} onChange={setStyles} testPrefix="style" />
      </div>

      <div className="mt-3" data-testid="curation-conditions">
        <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-1.5">Health conditions — plan follows ICMR / KDIGO / ADA / WHO guidance</div>
        <ChipSelect options={HEALTH_CONDITIONS} value={conds} onChange={setConds} testPrefix="cond" />
        {(conds.includes("Other Food Allergy") || conds.some((c) => c.includes("Allergy"))) && (
          <Input data-testid="curation-allergy" placeholder="Mention the exact food(s) the client is allergic to…" value={allergy}
            onChange={(e) => setAllergy(e.target.value)} className="mt-2 bg-[#F7F5F0] border-[#E5E2D9] h-9 text-xs" />
        )}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        {days.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#5C665A] w-12 shrink-0">Day {i + 1}</span>
            <select data-testid={`curation-day-${i + 1}`} value={v}
              onChange={(e) => {
                const d = [...days]; d[i] = e.target.value; setDays(d);
                if (e.target.value !== "indian") { const r = [...regions]; r[i] = ""; setRegions(r); }
              }}
              className="flex-1 bg-[#F7F5F0] border border-[#E5E2D9] rounded-xl h-9 px-2 text-xs">
              {ADMIN_CUISINES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
            {v === "indian" && (
              <select data-testid={`curation-region-${i + 1}`} value={regions[i]}
                onChange={(e) => { const r = [...regions]; r[i] = e.target.value; setRegions(r); }}
                className="w-36 shrink-0 bg-white border border-[#C57B57]/40 rounded-xl h-9 px-2 text-xs text-[#AD6A48]">
                <option value="">Pan-Indian</option>
                {INDIAN_REGIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <Input data-testid="curation-title" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-[#F7F5F0] border-[#E5E2D9] h-10 max-w-xs" />
        <button data-testid="curation-generate" onClick={generate} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 bg-[#C57B57] text-white text-xs font-medium hover:bg-[#AD6A48] disabled:opacity-60">
          {busy ? <><Loader2 size={13} className="animate-spin" /> {customised ? "AI-curating custom plan… (up to a minute)" : "Generating…"}</> : <><Wand2 size={13} /> Generate & send to client</>}
        </button>
      </div>
      <p className="mt-2 text-[10px] text-[#9AA294]">
        {customised
          ? "Custom mode: AI builds a 7-day mixed-cuisine plan honouring your targets, dietary styles, allergies & health conditions — then saves the branded PDF to the client's dashboard and notifies them."
          : "Standard mode: builds a branded 7-day PDF from the Global Series library, saves it to the client's dashboard and notifies them by email + WhatsApp."}
      </p>
    </div>
  );
};
